import React from "react";
import { Link, useLocation } from "wouter";
import { X, Tally4, LayoutDashboard, FolderOpen, Building, UserCog, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const [location] = useLocation();
  const { user: currentUser } = useAuth();
  
  // Navigation items
  const mainNavItems = [
    { href: "/", icon: LayoutDashboard, label: "Dashboard" },
    { href: "/projects", icon: FolderOpen, label: "Portfolio" },
    { href: "/organizations", icon: Building, label: "Organizations" },
  ];

  const settingsNavItems = [
    { href: "/admin/users", icon: UserCog, label: "User Management" },
    { href: "/settings", icon: Settings, label: "Tenant Management" },
  ];

  // Show Tenant Management for SuperAdmins and other relevant roles.
  // SuperAdmins should always see it.
  // TenantAdmins should also see it.
  const visibleSettingsNavItems = settingsNavItems.filter(item => {
    if (!currentUser) return false;
    if (item.href === "/admin/users") {
      return currentUser.isSuperAdmin || currentUser.role === 'TenantAdmin';
    }
    if (item.href === "/settings") {
      return currentUser.isSuperAdmin || currentUser.role === 'TenantAdmin';
    }
    return true;
  });

  // Check if a nav item is active
  const isActive = (path: string) => {
    if (path === "/" && location === "/") return true;
    if (path !== "/" && location.startsWith(path)) return true;
    return false;
  };

  if (!isOpen) return null;

  return (
    <div className="md:hidden fixed inset-0 z-40 flex">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-neutral-800 bg-opacity-75" 
        aria-hidden="true"
        onClick={onClose}
      ></div>
      
      {/* Menu content */}
      <div className="relative flex-1 flex flex-col max-w-xs w-full bg-neutral-800 shadow-xl">
        <div className="absolute top-0 right-0 -mr-12 pt-2">
          <button 
            className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            onClick={onClose}
          >
            <span className="sr-only">Close sidebar</span>
            <X className="text-white text-2xl" />
          </button>
        </div>
        
        <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
          <div className="px-4 pb-3">
            <div className="flex items-center">
              <Tally4 className="text-primary text-2xl mr-2" />
              <span className="text-xl font-semibold text-white">Vertrage</span>
            </div>
            <div className="mt-2 pl-1">
              <div className="flex items-center">
                <div className="w-1 h-4 bg-primary rounded-full mr-2"></div>
                <span className="text-sm font-medium text-neutral-300 tracking-wide">
                  {"Cascade Grid Holdings"}
                </span>
              </div>
            </div>
          </div>
          
          <nav className="mt-5 px-2 space-y-1">
            {mainNavItems.map((item) => (
              <div key={item.href}>
                <Link href={item.href} 
                  className={cn(
                    "flex items-center px-3 py-2 rounded-md text-sm font-medium",
                    isActive(item.href) 
                      ? "bg-primary text-white" 
                      : "text-white hover:bg-neutral-700"
                  )}
                  onClick={onClose}
                >
                  <item.icon className="mr-3 text-lg" />
                  <span>{item.label}</span>
                </Link>
              </div>
            ))}
            
            {/* Only show Settings section if there are visible items */}
            {visibleSettingsNavItems.length > 0 && (
              <div className="pt-4 mt-4 border-t border-neutral-700">
                <h3 className="px-3 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                  Settings
                </h3>
                <div className="mt-2 space-y-1">
                  {visibleSettingsNavItems.map((item) => (
                    <div key={item.href}>
                      <Link href={item.href}
                        className={cn(
                          "flex items-center px-3 py-2 rounded-md text-sm font-medium",
                          isActive(item.href) 
                            ? "bg-primary text-white" 
                            : "text-white hover:bg-neutral-700"
                        )}
                        onClick={onClose}
                      >
                        <item.icon className="mr-3 text-lg" />
                        <span>{item.label}</span>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </nav>
        </div>
      </div>
      
      <div className="flex-shrink-0 w-14" aria-hidden="true">
        {/* Dummy element to force sidebar to shrink to fit close icon */}
      </div>
    </div>
  );
}
